'use strict';

/**
 * Hosts the Doofinder "embedded results" layer.
 *
 * On desktop, when the visitor validates a search with the Enter key, Doofinder
 * stops floating over the page and moves its layer root (`.dfd-root`) into the
 * insertion point configured in the Doofinder admin, which lives inside this
 * element (`#doofinder-embedded-results`).
 *
 * This element watches for that root. While it is shown, `<main>` gets a state
 * class so the theme can hide the page sections behind the results and keep only
 * the header and the footer. When Doofinder hides its root again (visitor closes
 * the results or starts a new floating search), everything is restored.
 *
 * Nothing here talks to Doofinder: the element only reacts to the DOM Doofinder
 * produces, so the page degrades to the native Shopify search if their script
 * never loads.
 *
 * One exception: Doofinder keeps its layer state in `history.state` and restores
 * it on load. A link to the page the visitor is already on (typically the logo
 * while embedded results cover the home) reuses the same history entry, so the
 * results would come straight back. While results are shown, clicking such a
 * link drops that state first, so the page loads clean.
 *
 * @class
 * @extends HTMLElement
 */
class AxDoofinderEmbedded extends HTMLElement {
  // Selectors
  #selectors = {
    root: '.dfd-root',
    main: '#main'
  };

  // CSS classes
  #classes = {
    open: 'doofinder-embedded--open',
    mainOpen: 'main--search-embedded'
  };

  // Doofinder marks the screen type on its root; only this one belongs in the page
  #embeddedScreen = 'embedded';

  // Key under which Doofinder persists its layer state in history.state
  #historyStateKey = 'dfState';

  // Cached elements
  #elements = {};

  /** @type {MutationObserver|null} */
  #observer = null;

  /** @type {boolean} */
  #isOpen = false;

  /**
   * Lifecycle - Component connected to DOM
   * @private
   */
  connectedCallback() {
    this.#cacheElements();

    if (!this.#isValidSetup()) return;

    this.#observer = new MutationObserver(this.#handleMutation);
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'data-dfd-screen']
    });

    // Capture phase, so the state is dropped before the navigation starts
    document.addEventListener('click', this.#handleDocumentClick, true);

    this.#update();
  }

  /**
   * Lifecycle - Component removed from DOM
   * @private
   */
  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    document.removeEventListener('click', this.#handleDocumentClick, true);
    this.#setOpen(false);
  }

  /**
   * Cache DOM elements
   * @private
   */
  #cacheElements() {
    this.#elements = {
      main: document.querySelector(this.#selectors.main)
    };
  }

  /**
   * Validate required elements exist
   * @private
   * @returns {boolean}
   */
  #isValidSetup() {
    if (!this.#elements.main) {
      console.error('AxDoofinderEmbedded: <main id="main"> not found');
      return false;
    }

    return true;
  }

  /**
   * Re-evaluate the state after any change Doofinder makes inside this element
   * @private
   */
  #handleMutation = () => {
    this.#update();
  };

  /**
   * A click on a link to the current page while results are shown means "give me
   * the page back", not "show me the results again"
   * @private
   * @param {MouseEvent} event - Click event
   */
  #handleDocumentClick = (event) => {
    if (!this.#isOpen) return;

    const link = event.target.closest('a[href]');

    if (!link || !this.#isLinkToCurrentPage(link)) return;

    this.#forgetDoofinderHistoryState();
  };

  /**
   * Whether a link targets the page currently displayed (hash ignored)
   * @private
   * @param {HTMLAnchorElement} link - The clicked link
   * @returns {boolean}
   */
  #isLinkToCurrentPage(link) {
    let url;

    try {
      url = new URL(link.href, location.href);
    } catch (error) {
      return false;
    }

    return url.origin === location.origin
      && url.pathname === location.pathname
      && url.search === location.search;
  }

  /**
   * Remove the Doofinder entry from history.state, leaving any other state intact
   * @private
   */
  #forgetDoofinderHistoryState() {
    const state = history.state;

    if (!state || typeof state !== 'object' || !(this.#historyStateKey in state)) return;

    const { [this.#historyStateKey]: _dropped, ...rest } = state;

    history.replaceState(Object.keys(rest).length ? rest : null, '', location.href);
  }

  /**
   * Whether Doofinder is currently showing embedded results in this element.
   *
   * Doofinder hides its root with the `hidden` attribute when closed. The root is
   * only ever moved in here for embedded results, so a missing screen marker is
   * treated as embedded rather than leaving the results stranded.
   *
   * @private
   * @returns {boolean}
   */
  #hasOpenEmbeddedRoot() {
    const root = this.querySelector(this.#selectors.root);

    if (!root || root.hasAttribute('hidden')) return false;

    const screen = root.getAttribute('data-dfd-screen');

    return screen === null || screen === this.#embeddedScreen;
  }

  /**
   * Sync the state classes with the DOM Doofinder produced
   * @private
   */
  #update() {
    const isOpen = this.#hasOpenEmbeddedRoot();

    if (isOpen === this.#isOpen) return;

    this.#setOpen(isOpen);

    if (isOpen) this.#scrollToTop();
  }

  /**
   * Apply or remove the state classes
   * @private
   * @param {boolean} isOpen - Whether embedded results are shown
   */
  #setOpen(isOpen) {
    this.#isOpen = isOpen;
    this.classList.toggle(this.#classes.open, isOpen);
    this.#elements.main?.classList.toggle(this.#classes.mainOpen, isOpen);
  }

  /**
   * Bring the results into view: the visitor may have validated the search while
   * scrolled far down a page whose sections just got hidden
   * @private
   */
  #scrollToTop() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('ax-doofinder-embedded')) {
  customElements.define('ax-doofinder-embedded', AxDoofinderEmbedded);
}
